import React, { useState, useCallback, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, ActivityIndicator, TextInput, Platform } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet, apiPost } from '../../src/api/client';
import { useToast } from '../../src/components/Toast';
import { formatDateTime } from '../../src/utils/timezone';
import Card from '../../src/components/Card';
import Button from '../../src/components/Button';
import ContentContainer from '../../src/components/ContentContainer';
import { useAuth } from '../../src/context/AuthContext';
import QRScannerModal from '../../src/components/QRScannerModal';
import ReportProblemModal from '../../src/components/ReportProblemModal';
import RewardsLists from '../../src/components/RewardsLists';
import VendingCompCard from '../../src/components/VendingCompCard';
import InlineConfirm from '../../src/components/InlineConfirm';
import { useTheme } from '../../src/context/ThemeContext';
import { useNetwork } from '../../src/context/NetworkContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useResponsive, rs, fs } from '../../src/utils/responsive';

const RATABLE_TYPES = ['automatic', 'self-serve', 'bundle-wash', 'subscription-wash'];
function toRatingWashType(txType: string): string {
  if (txType === 'bundle-wash') return 'bundle';
  if (txType === 'subscription-wash') return 'subscription';
  return txType;
}

interface PersonalTransaction {
  readonly id: string;
  readonly type: string;
  readonly tierName: string | null;
  readonly durationMinutes: number | null;
  readonly amount: number;
  readonly status: string;
  readonly createdAt: string;
  readonly locationName: string | null;
  readonly bayName: string | null;
  readonly rating: number | null;
  readonly reported?: boolean;
}

interface Location {
  id: string;
  name: string;
  address: string;
}

interface Bay {
  id: string;
  name: string;
  type: string;
  tiers: Tier[] | null;
  pricePerMinute: number | null;
  controllerId: string | null;
  isActive: boolean;
}

interface Tier {
  name: string;
  price: number;
  /** Vacuum packages are timed products; minutes come from durationMs. */
  durationMs?: number;
  enabled?: boolean;
}

interface ActiveSession {
  active: boolean;
  sessionId?: string;
  bayName?: string;
  bayType?: string;
  locationName?: string;
  startedAt?: string;
}


/** OwnerWashScreen - start complimentary washes or scan QR for other locations. */
export default function OwnerWashScreen() {
  const { colors } = useTheme();
  const { isDesktopWeb } = useResponsive();
  const styles = useStyles(colors);
  const toast = useToast();
  const router = useRouter();

  // Purchase wash (at other car washes)
  const [manualUrl, setManualUrl] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleManualEntry = () => {
    const raw = manualUrl.trim();
    const washMatch = raw.match(/wash\/([^/\s?#]+)\/([^/\s?#]+)/);
    if (washMatch) {
      router.push(`/wash/${washMatch[1]}/${washMatch[2]}` as never);
      return;
    }
    // Promo/gift code (redeem link or bare code) → redemption screen.
    const redeemMatch = raw.match(/redeem\/([^/\s?#]+)/i);
    if (redeemMatch) {
      router.push(`/redeem/${redeemMatch[1].toUpperCase()}` as never);
      return;
    }
    if (/^[A-Za-z0-9]{4,20}$/.test(raw)) {
      router.push(`/redeem/${raw.toUpperCase()}` as never);
      return;
    }
    toast.show({ type: 'error', title: 'Not recognized', message: 'Enter a wash URL or a promo/gift code' });
  };

  const handleQRScanned = (data: string) => {
    setScannerOpen(false);
    const match = data.match(/wash\/([^/\s?#]+)\/([^/\s?#]+)/);
    if (match) {
      router.push(`/wash/${match[1]}/${match[2]}` as never);
    } else {
      toast.show({ type: 'error', title: 'Invalid QR Code', message: 'This QR code is not a valid RinsePay wash code' });
    }
  };

  const openScanner = () => {
    if (Platform.OS === 'web' && isDesktopWeb) {
      toast.show({ type: 'info', title: 'Camera', message: 'QR scanning is available on the mobile app. Use the manual entry below on web.' });
    } else {
      setScannerOpen(true);
    }
  };

  // Active session state (complimentary washes)
  const [activeSession, setActiveSession] = useState<ActiveSession | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Purchased wash session (at other car washes)
  const [purchasedSession, setPurchasedSession] = useState<{
    active: boolean; sessionId?: string; locationId?: string; bayId?: string;
    bayName?: string; locationName?: string; isPreset?: boolean;
    presetEndsAt?: string; currentCostCents?: number; startedAt?: string;
  } | null>(null);
  const [purchasedRemaining, setPurchasedRemaining] = useState<number | null>(null);
  const purchasedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start wash form state
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [bays, setBays] = useState<Bay[]>([]);
  const [selectedBayId, setSelectedBayId] = useState<string | null>(null);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [loadingBays, setLoadingBays] = useState(false);

  // Personal wash history
  const [personalTxs, setPersonalTxs] = useState<PersonalTransaction[]>([]);
  const [reportTx, setReportTx] = useState<PersonalTransaction | null>(null);
  const [ratingItemId, setRatingItemId] = useState<string | null>(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [txSearch, setTxSearch] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [txLimit, setTxLimit] = useState(10);
  const [txTotal, setTxTotal] = useState(0);
  const [txExporting, setTxExporting] = useState(false);
  const txSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedBay = bays.find(b => b.id === selectedBayId);
  // Round 2 (S06): owners can comp a wash with upsells. Upsells come from the bay detail (server-priced actions).
  const [bayUpsells, setBayUpsells] = useState<{ actionId: string; name: string; priceCents: number }[]>([]);
  const [selectedUpsells, setSelectedUpsells] = useState<string[]>([]);
  useEffect(() => {
    setSelectedUpsells([]); setBayUpsells([]);
    if (!selectedBayId || selectedBay?.type !== 'automatic') return;
    apiGet<{ upsells?: { actionId: string; name: string; priceCents: number }[] }>(`/api/bays/${selectedBayId}`).then(b => setBayUpsells(b.upsells ?? [])).catch(() => setBayUpsells([]));
  }, [selectedBayId, selectedBay?.type]);

  const submitRating = async (item: PersonalTransaction, stars: number) => {
    setRatingSubmitting(true);
    try {
      await api('/api/ratings', { method: 'POST', body: { washType: toRatingWashType(item.type), washId: item.id, rating: stars } });
      setPersonalTxs(prev => prev.map(t => t.id === item.id ? { ...t, rating: stars } : t));
      setRatingItemId(null);
    } catch { toast.show({ type: 'error', title: 'Error', message: 'Could not submit rating' }); }
    finally { setRatingSubmitting(false); }
  };

  const fetchPersonalTxPaged = async (p = txPage, l = txLimit, s = txSearch) => {
    try {
      const params = new URLSearchParams({ personal: 'true', page: String(p), limit: String(l) });
      if (s.trim()) params.set('search', s.trim());
      const data = await api<{ transactions: PersonalTransaction[]; total: number; page: number; limit: number }>(
        `/api/transactions?${params}`
      );
      setPersonalTxs(data.transactions);
      setTxTotal(data.total);
      setTxPage(data.page);
      setTxLimit(data.limit);
    } catch { /* ignore */ }
  };

  const handleTxSearch = (text: string) => {
    setTxSearch(text);
    if (txSearchTimer.current) clearTimeout(txSearchTimer.current);
    txSearchTimer.current = setTimeout(() => { fetchPersonalTxPaged(1, txLimit, text); }, 400);
  };

  const getTxLabel = (tx: PersonalTransaction) => {
    if (tx.type === 'automatic') return tx.tierName || 'Wash';
    if (tx.type === 'bundle-wash') return 'Bundle Wash';
    if (tx.type === 'subscription-wash') return 'Member Wash';
    if (tx.type === 'bundle-purchase') return `Bundle: ${tx.tierName || 'Purchase'}`;
    const totalSecs = Math.round((tx.durationMinutes || 0) * 60);
    return `${Math.floor(totalSecs / 60)}:${(totalSecs % 60).toString().padStart(2, '0')} wash`;
  };

  const handleExportCsv = async () => {
    setTxExporting(true);
    try {
      const allData = await api<PersonalTransaction[]>('/api/transactions?personal=true');
      const rows = allData.map(tx => ({
        Date: formatDateTime(tx.createdAt), Type: getTxLabel(tx), Status: tx.status,
        Amount: (tx.amount / 100).toFixed(2), Location: tx.locationName || '', Bay: tx.bayName || '',
        Rating: tx.rating != null ? String(tx.rating) : '',
      }));
      const headers = Object.keys(rows[0] || {});
      const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${(r as any)[h] || ''}"`).join(','))].join('\n');
      const { exportCsvFromString } = await import('../../src/utils/fileExport');
      await exportCsvFromString(csv, 'wash-history.csv');
    } catch (err) { console.error('CSV export failed:', err); }
    finally { setTxExporting(false); }
  };

  // Fetch active complimentary session
  const fetchActive = async () => {
    try {
      const data = await apiGet<ActiveSession>('/api/owner/wash/active');
      setActiveSession(data);
    } catch { /* non-critical */ }
  };

  // Fetch active purchased session
  const fetchPurchasedSession = async () => {
    try {
      const data = await api<any>('/api/wash-sessions/active');
      setPurchasedSession(data);
    } catch { /* non-critical */ }
  };

  // Fetch personal wash history
  const fetchPersonalHistory = async () => {
    fetchPersonalTxPaged(1, 10, '');
  };

  // Fetch locations
  const fetchLocations = async () => {
    try {
      const data = await apiGet<Location[]>('/api/locations');
      setLocations(data);
    } catch (err) { console.error('fetch locations:', err); }
  };

  // Fetch bays for selected location
  const fetchBays = async (locationId: string, signal?: AbortSignal) => {
    setLoadingBays(true);
    try {
      const data = await apiGet<Bay[]>(`/api/locations/${locationId}/bays`);
      if (signal?.aborted) return;
      setBays(data.filter(b => b.isActive));
    } catch { /* bay fetch failed — show empty list */
      if (signal?.aborted) return;
      setBays([]);
    } finally {
      if (!(signal?.aborted)) setLoadingBays(false);
    }
  };

  useFocusEffect(useCallback(() => {
    fetchActive();
    fetchPurchasedSession();
    fetchPersonalHistory();
    fetchLocations();
    const interval = setInterval(() => { fetchActive(); fetchPurchasedSession(); }, 5000);
    return () => clearInterval(interval);
  }, []));

  // When location changes, fetch bays
  useEffect(() => {
    const ac = new AbortController();
    setSelectedBayId(null);
    setTiers([]);
    setSelectedTier(null);
    if (selectedLocationId) {
      fetchBays(selectedLocationId, ac.signal);
    } else {
      setBays([]);
    }
    return () => ac.abort();
  }, [selectedLocationId]);

  // When bay changes, set tiers. Automatic bays list wash options; package
  // vacuums list their timed packages (the comp start REQUIRES tierName for
  // those — mig 0064). Metered vacs (rate, no packages) run count-up like
  // self-serve with no picker.
  useEffect(() => {
    setSelectedTier(null);
    if (selectedBay?.type === 'automatic' && selectedBay.tiers?.length) {
      setTiers(selectedBay.tiers);
    } else if (selectedBay?.type === 'vacuum' && selectedBay.tiers?.length) {
      setTiers(selectedBay.tiers.filter(t => t.enabled !== false && t.durationMs));
    } else {
      setTiers([]);
    }
  }, [selectedBayId]);

  // Elapsed timer for active session
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (activeSession?.active && activeSession.startedAt) {
      const update = () => {
        const ms = Date.now() - new Date(activeSession.startedAt!).getTime();
        // Comp washes auto-end at 15 min on the backend; freeze the display
        // there and let the active poll clear the card once it closes.
        setElapsed(Math.min(Math.floor(ms / 1000), 900));
      };
      update();
      timerRef.current = setInterval(update, 1000);
    } else {
      setElapsed(0);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeSession?.active, activeSession?.startedAt]);

  // Purchased session countdown/cost timer
  useEffect(() => {
    if (purchasedTimerRef.current) clearInterval(purchasedTimerRef.current);
    if (purchasedSession?.active && purchasedSession.isPreset && purchasedSession.presetEndsAt) {
      const tick = () => {
        const rem = Math.max(0, Math.ceil((new Date(purchasedSession.presetEndsAt!).getTime() - Date.now()) / 1000));
        setPurchasedRemaining(rem);
      };
      tick();
      purchasedTimerRef.current = setInterval(tick, 1000);
    } else {
      setPurchasedRemaining(null);
    }
    return () => { if (purchasedTimerRef.current) clearInterval(purchasedTimerRef.current); };
  }, [purchasedSession?.sessionId, purchasedSession?.isPreset, purchasedSession?.presetEndsAt]);

  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleStartWash = async () => {
    if (!selectedBayId) return;
    const isVac = selectedBay?.type === 'vacuum';
    if (selectedBay?.type === 'automatic' && !selectedTier) {
      toast.show({ type: 'error', title: 'Select Wash', message: 'Please select a wash option' });
      return;
    }
    // Package vacs require a package (the start endpoint 400s with {tiers}
    // otherwise — we read bay.tiers up front so this never fires in practice).
    if (isVac && tiers.length > 0 && !selectedTier) {
      toast.show({ type: 'error', title: 'Select Package', message: 'Please select a vacuum package' });
      return;
    }
    setStarting(true);
    try {
      const body: any = { bayId: selectedBayId };
      if (selectedTier) body.tierName = selectedTier;
      if (selectedUpsells.length) body.upsellActionIds = selectedUpsells;
      const result = await apiPost<{ bayName: string; locationName: string }>('/api/owner/wash/start', body);
      toast.show({ type: 'success', title: isVac ? 'Vacuum Started' : 'Wash Started', message: `${result.bayName} at ${result.locationName}` });
      setSelectedBayId(null);
      setSelectedLocationId(null);
      setSelectedTier(null);
      setSelectedUpsells([]);
      fetchActive();
      fetchPersonalHistory();
    } catch (e: any) {
      toast.show({ type: 'error', title: 'Failed', message: e.message });
    } finally {
      setStarting(false);
    }
  };

  const handleStopWash = async () => {
    if (!activeSession?.sessionId) return;
    setStopping(true);
    try {
      const result = await apiPost<{ message: string }>('/api/owner/wash/stop', { sessionId: activeSession.sessionId });
      toast.show({ type: 'success', title: 'Wash Stopped', message: result.message });
      fetchActive();
      fetchPersonalHistory();
    } catch (e: any) {
      toast.show({ type: 'error', title: 'Failed', message: e.message });
    } finally {
      setStopping(false);
    }
  };

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
        {/* Active Wash Card */}
        {activeSession?.active && (
          <Card style={styles.activeCard}>
            <View style={styles.activeHeader}>
              <View style={styles.activePulse} />
              <Text style={styles.activeTitle}>
                {activeSession.bayType === 'vacuum' ? 'Vacuum in Progress' : 'Wash in Progress'}
              </Text>
            </View>
            <Text style={styles.activeLocation}>{activeSession.locationName} — {activeSession.bayName}</Text>
            <Text style={styles.activeTimer}>{formatElapsed(elapsed)}</Text>
            {/* Count-up comps are stoppable: self-serve bays and metered vacs.
                (Package-vac comps complete instantly and never show here.) */}
            {(activeSession.bayType === 'self-serve' || activeSession.bayType === 'vacuum') && (
              <TouchableOpacity
                style={styles.stopButton}
                onPress={handleStopWash}
                disabled={stopping}
              >
                {stopping ? (
                  <ActivityIndicator color={colors.white} size="small" />
                ) : (
                  <>
                    <Ionicons name="stop-circle" size={20} color={colors.white} />
                    <Text style={styles.stopButtonText}>
                      {activeSession.bayType === 'vacuum' ? 'Stop Vacuum' : 'Stop Wash'}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            )}
          </Card>
        )}

        {/* Purchased wash in progress banner */}
        {purchasedSession?.active && (
          <TouchableOpacity onPress={() => {
            if (purchasedSession.locationId && purchasedSession.bayId) {
              router.push(`/wash/${purchasedSession.locationId}/${purchasedSession.bayId}` as never);
            }
          }}>
            <Card style={styles.activeCard}>
              <View style={styles.activeHeader}>
                <View style={styles.activePulse} />
                <Text style={styles.activeTitle}>Purchased Wash in Progress</Text>
              </View>
              <Text style={styles.activeLocation}>{purchasedSession.locationName} — {purchasedSession.bayName}</Text>
              {purchasedSession.isPreset && purchasedRemaining != null ? (
                <Text style={styles.activeTimer}>{Math.floor(purchasedRemaining / 60)}:{(purchasedRemaining % 60).toString().padStart(2, '0')}</Text>
              ) : (
                <Text style={styles.activeTimer}>${((purchasedSession.currentCostCents || 0) / 100).toFixed(2)}</Text>
              )}
            </Card>
          </TouchableOpacity>
        )}

        {/* Purchase Wash at Another Car Wash */}
        <Card style={styles.purchaseCard}>
          <View style={styles.heroRow}>
            <Ionicons name="scan" size={28} color={colors.primary} />
            <Text style={styles.purchaseTitle}>Purchase Wash</Text>
          </View>
          <Text style={styles.formSubtitle}>Scan a QR code to wash at another car wash</Text>

          {Platform.OS === 'web' && isDesktopWeb ? (
            <View style={styles.webCameraNote}>
              <Ionicons name="phone-portrait-outline" size={16} color="#dc2626" />
              <Text style={styles.webCameraText}>Camera scanning available on mobile app</Text>
            </View>
          ) : (
            <>
              <TouchableOpacity style={styles.scanButton} onPress={openScanner}>
                <Ionicons name="camera" size={22} color={colors.white} />
                <Text style={styles.scanButtonText}>Open Scanner</Text>
              </TouchableOpacity>
              <QRScannerModal
                visible={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScanned={handleQRScanned}
              />
            </>
          )}

          <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or enter URL manually</Text>
            <View style={styles.dividerLine} />
          </View>

          <View style={styles.manualEntry}>
            <TextInput
              style={styles.manualInput}
              placeholder="Paste wash URL or code"
              placeholderTextColor={colors.textLight}
              value={manualUrl}
              onChangeText={setManualUrl}
              autoCapitalize="none"
              onSubmitEditing={handleManualEntry}
              returnKeyType="go"
              maxLength={255}
            />
            <TouchableOpacity style={styles.goBtn} onPress={handleManualEntry}>
              <Ionicons name="arrow-forward" size={20} color={colors.white} />
            </TouchableOpacity>
          </View>
        </Card>

        {/* Start Wash Form */}
        {!activeSession?.active && (
          <Card style={styles.formCard}>
            <View style={styles.heroRow}>
              <Ionicons name="water" size={36} color={colors.primary} />
              <Text style={styles.formTitle}>Start Wash</Text>
            </View>
            <Text style={styles.formSubtitle}>Start a complimentary wash in any of your bays</Text>

            {/* Location Picker */}
            <Text style={styles.label}>Location</Text>
            <View style={styles.chipRow}>
              {locations.map(loc => (
                <TouchableOpacity
                  key={loc.id}
                  style={[styles.chip, selectedLocationId === loc.id && styles.chipSelected]}
                  onPress={() => setSelectedLocationId(loc.id)}
                >
                  <Text style={[styles.chipText, selectedLocationId === loc.id && styles.chipTextSelected]}>
                    {loc.name}
                  </Text>
                </TouchableOpacity>
              ))}
              {locations.length === 0 && (
                <Text style={styles.emptyText}>No locations found</Text>
              )}
            </View>

            {/* Bay Picker */}
            {selectedLocationId && (
              <>
                <Text style={styles.label}>Bay</Text>
                {loadingBays ? (
                  <ActivityIndicator color={colors.primary} style={{ marginVertical: spacing.sm }} />
                ) : (
                  <View style={styles.chipRow}>
                    {bays.map(bay => (
                      <TouchableOpacity
                        key={bay.id}
                        style={[styles.chip, selectedBayId === bay.id && styles.chipSelected]}
                        onPress={() => setSelectedBayId(bay.id)}
                      >
                        <Text style={[styles.chipText, selectedBayId === bay.id && styles.chipTextSelected]}>
                          {bay.name}
                        </Text>
                        <Text style={[styles.chipSub, selectedBayId === bay.id && styles.chipSubSelected]}>
                          {bay.type}
                        </Text>
                      </TouchableOpacity>
                    ))}
                    {bays.length === 0 && (
                      <Text style={styles.emptyText}>No active bays</Text>
                    )}
                  </View>
                )}
              </>
            )}

            {/* Tier Picker — automatic wash options or vacuum packages */}
            {(selectedBay?.type === 'automatic' || selectedBay?.type === 'vacuum') && tiers.length > 0 && (
              <>
                <Text style={styles.label}>{selectedBay.type === 'vacuum' ? 'Vacuum Package' : 'Wash Option'}</Text>
                <View style={styles.chipRow}>
                  {tiers.map(tier => (
                    <TouchableOpacity
                      key={tier.name}
                      style={[styles.chip, selectedTier === tier.name && styles.chipSelected]}
                      onPress={() => setSelectedTier(tier.name)}
                    >
                      <Text style={[styles.chipText, selectedTier === tier.name && styles.chipTextSelected]}>
                        {tier.name}
                      </Text>
                      {selectedBay.type === 'vacuum' && tier.durationMs ? (
                        <Text style={[styles.chipSub, selectedTier === tier.name && styles.chipSubSelected]}>
                          {Math.round(tier.durationMs / 60000)} min
                        </Text>
                      ) : null}
                    </TouchableOpacity>
                  ))}
                </View>
              </>
            )}
            {selectedBay?.type === 'automatic' && bayUpsells.length > 0 && (
              <>
                <Text style={styles.label}>Upsells (included free with this comp)</Text>
                <View style={styles.chipRow}>
                  {bayUpsells.map(u => { const on = selectedUpsells.includes(u.actionId); return (
                    <TouchableOpacity key={u.actionId} style={[styles.chip, on && styles.chipSelected]} onPress={() => setSelectedUpsells(on ? selectedUpsells.filter(x => x !== u.actionId) : [...selectedUpsells, u.actionId])}>
                      <Text style={[styles.chipText, on && styles.chipTextSelected]}>{u.name}</Text>
                    </TouchableOpacity>
                  ); })}
                </View>
              </>
            )}
            {/* Metered vacs run count-up like self-serve — no package needed. */}
            {selectedBay?.type === 'vacuum' && tiers.length === 0 && (
              <Text style={[styles.emptyText, { marginTop: spacing.sm }]}>
                Metered vacuum — starts count-up, stop it when done.
              </Text>
            )}

            {/* Start Button */}
            <TouchableOpacity
              style={[styles.startButton, !selectedBayId && styles.startButtonDisabled]}
              onPress={handleStartWash}
              disabled={!selectedBayId || starting}
            >
              {starting ? (
                <ActivityIndicator color={colors.white} size="small" />
              ) : (
                <>
                  <Ionicons
                    name={selectedBay?.type === 'vacuum' ? 'sparkles' : 'play-circle'}
                    size={22}
                    color={colors.white}
                  />
                  <Text style={styles.startButtonText}>
                    {selectedBay?.type === 'vacuum' ? 'Start Vacuum' : 'Start Wash'}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </Card>
        )}

        {/* Vending — complimentary credit to a machine at the selected location */}
        <VendingCompCard locationId={selectedLocationId} compEndpoint="/api/owner/vending/comp" />

        {/* Personal Wash History */}
        {(personalTxs.length > 0 || txTotal > 0) && (
          <Card style={styles.historySection}>
            <Text style={styles.sectionTitle}>Wash History</Text>

            <View style={styles.txToolbar}>
              <View style={styles.txSearchWrap}>
                <Ionicons name="search" size={16} color={colors.textLight} />
                <TextInput style={styles.txSearchInput} placeholder="Search..." placeholderTextColor={colors.textLight}
                  value={txSearch} onChangeText={handleTxSearch} returnKeyType="search" maxLength={100} />
                {txSearch ? <TouchableOpacity onPress={() => { setTxSearch(''); fetchPersonalTxPaged(1, txLimit, ''); }} hitSlop={8}><Ionicons name="close-circle" size={16} color={colors.textLight} /></TouchableOpacity> : null}
              </View>
              <TouchableOpacity onPress={handleExportCsv} style={styles.txCsvBtn} disabled={txExporting}>
                <Ionicons name="download-outline" size={16} color={colors.white} />
                <Text style={styles.txCsvText}>{txExporting ? '...' : 'CSV'}</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.txCount}>{txTotal} wash{txTotal !== 1 ? 'es' : ''}</Text>

            {personalTxs.map(item => (
              <View key={item.id} style={styles.txRow}>
                <View style={[styles.txIcon, { backgroundColor: colors.primary + '15' }]}>
                  <Ionicons
                    name={item.type === 'automatic' ? 'car' : item.type === 'vacuum' ? 'sparkles' : item.type === 'vending' ? 'fast-food' : 'timer'}
                    size={16}
                    color={colors.primary}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.txTitle}>
                    {getTxLabel(item)}{item.bayName ? ` · ${item.bayName}` : ''}
                  </Text>
                  <Text style={styles.txDetail}>
                    {item.locationName || ''} · {formatDateTime(item.createdAt)}
                  </Text>
                  {ratingItemId === item.id ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 2 }}>
                      {ratingSubmitting ? <ActivityIndicator size="small" color={colors.primary} /> :
                        [1,2,3,4,5].map(s => <TouchableOpacity key={s} onPress={() => submitRating(item, s)} hitSlop={4}><Ionicons name={s <= (item.rating ?? 0) ? 'star' : 'star-outline'} size={20} color={colors.warning} /></TouchableOpacity>)}
                    </View>
                  ) : item.rating != null ? (
                    <TouchableOpacity onPress={() => RATABLE_TYPES.includes(item.type) && setRatingItemId(item.id)} disabled={!RATABLE_TYPES.includes(item.type)} style={{ flexDirection: 'row', marginTop: 2 }}>
                      {[1,2,3,4,5].map(s => <Ionicons key={s} name={s <= item.rating! ? 'star' : 'star-outline'} size={11} color={colors.warning} />)}
                    </TouchableOpacity>
                  ) : RATABLE_TYPES.includes(item.type) ? (
                    <TouchableOpacity onPress={() => setRatingItemId(item.id)} style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
                      <Ionicons name="star-outline" size={11} color={colors.primary} />
                      <Text style={{ fontSize: fontSize.xs, color: colors.primary, marginLeft: 2 }}>Rate</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
                <View style={{ alignItems: 'flex-end', gap: 4 }}>
                  <Text style={styles.txAmount}>${(item.amount / 100).toFixed(2)}</Text>
                  {item.reported ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3 }}>
                      <Ionicons name="flag" size={14} color={colors.danger} />
                      <Text style={{ fontSize: fontSize.xs, color: colors.danger, fontWeight: fontWeight.semibold as any }}>Reported</Text>
                    </View>
                  ) : (
                    <TouchableOpacity onPress={() => setReportTx(item)} hitSlop={8}>
                      <Ionicons name="flag-outline" size={14} color={colors.danger} />
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            ))}

            {/* Always 10/page pagination — search hits the backend so the
                returned total + page reflect the filter. */}
            {txTotal > 10 && (() => {
              const totalPages = Math.max(1, Math.ceil(txTotal / 10));
              return (
                <View style={styles.pageNav}>
                  <TouchableOpacity
                    onPress={() => fetchPersonalTxPaged(txPage - 1, 10, txSearch)}
                    disabled={txPage <= 1}
                    style={[styles.pageBtn, txPage <= 1 && { opacity: 0.4 }]}
                  >
                    <Ionicons name="chevron-back" size={16} color={colors.primary} />
                    <Text style={styles.pageBtnText}>Prev</Text>
                  </TouchableOpacity>
                  <Text style={styles.pageInfo}>Page {txPage} of {totalPages}</Text>
                  <TouchableOpacity
                    onPress={() => fetchPersonalTxPaged(txPage + 1, 10, txSearch)}
                    disabled={txPage >= totalPages}
                    style={[styles.pageBtn, txPage >= totalPages && { opacity: 0.4 }]}
                  >
                    <Text style={styles.pageBtnText}>Next</Text>
                    <Ionicons name="chevron-forward" size={16} color={colors.primary} />
                  </TouchableOpacity>
                </View>
              );
            })()}
          </Card>
        )}
        <OwnerCustomerRewardsSection />
      </ContentContainer>
      {reportTx && (
        <ReportProblemModal
          visible={!!reportTx}
          onClose={() => setReportTx(null)}
          transactionId={reportTx.id}
          locationName={reportTx.locationName || undefined}
          bayName={reportTx.bayName || undefined}
          amount={reportTx.amount}
          date={formatDateTime(reportTx.createdAt)}
          type={reportTx.type}
          tierName={reportTx.tierName || undefined}
          durationMinutes={reportTx.durationMinutes || undefined}
          onSuccess={() => setPersonalTxs(prev => prev.map(t => t.id === reportTx.id ? { ...t, reported: true } : t))}
        />
      )}
    </ScrollView>
  );
}

/**
 * Owner-as-customer rewards. Surfaces wash credit / vouchers / points the
 * owner has accumulated at OTHER washes (filters out their own ownerId so
 * the section is empty when they only have rewards from themselves). Also
 * exposes a Send-Gift CTA that routes to the standard customer send flow,
 * not the owner-scoped form (owners gifting to other owners' customers).
 */
interface OtherWashSubscription {
  id: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  washesUsedToday: number;
  washesUsedThisMonth: number;
  minutesUsedToday: number | null;
  minutesUsedThisMonth: number | null;
  ownerId: string;
  ownerBusinessName: string;
  plan: {
    name: string;
    planType?: 'automatic' | 'self_serve' | 'mixed';
    maxWashesPerDay: number | null;
    maxWashesPerMonth: number | null;
    maxMinutesPerDay: number | null;
    maxMinutesPerMonth: number | null;
  } | null;
}

function OwnerCustomerRewardsSection() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const toast = useToast();
  const { isOffline } = useNetwork();
  const { user } = useAuth() as any;
  const ownerId = user?.id as string | undefined;

  const [anyRewards, setAnyRewards] = useState(false);
  const [subs, setSubs] = useState<OtherWashSubscription[]>([]);
  const [canceling, setCanceling] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);

  // Credits/vouchers/points render via the shared RewardsLists (filtered to
  // OTHER owners — self-gifting makes no sense). This callback only feeds the
  // empty state.
  const handleRewardsLoaded = useCallback((d: { credits: unknown[]; points: unknown[]; vouchers: unknown[] }) => {
    setAnyRewards(d.credits.length + d.points.length + d.vouchers.length > 0);
  }, []);

  // Active memberships the owner holds as a customer. Unlike rewards, we show
  // ALL live subscriptions including one bought at the owner's own wash, so
  // they can always view counters and cancel from here.
  const loadRewards = useCallback(async () => {
    try {
      const sub = await apiGet<OtherWashSubscription[]>('/api/subscriptions/mine');
      setSubs(sub.filter(x => x.status !== 'canceled'));
    } catch {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Couldn’t load your memberships' });
      }
    }
  }, [isOffline, toast]);

  useFocusEffect(useCallback(() => { loadRewards(); }, [loadRewards]));

  const handleCancelSub = async (sub: OtherWashSubscription) => {
    setCanceling(sub.id);
    try {
      await apiPost(`/api/subscriptions/${sub.id}/cancel`);
      toast.show({ type: 'success', title: 'Membership canceled', message: 'Access continues until the end of your billing period.' });
      await loadRewards();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err?.message || 'Could not cancel membership' });
    } finally {
      setCanceling(null);
    }
  };

  return (
    <View style={{ marginTop: spacing.lg }}>
      <Text style={styles.sectionTitle}>Rewards at Other Washes</Text>
      {!anyRewards && (
        <Card style={{ padding: spacing.lg, alignItems: 'center' }}>
          <Text style={{ color: colors.textSecondary, textAlign: 'center', fontSize: fontSize.sm }}>
            No rewards from other washes yet. Buy a gift, scan a promo QR, or get sent a wash credit by a friend.
          </Text>
        </Card>
      )}
      <RewardsLists excludeOwnerId={ownerId} onLoaded={handleRewardsLoaded} warnOnError={!isOffline} />
      <Button
        title="Purchase a Gift Wash"
        icon={<Ionicons name="gift" size={20} color={colors.white} />}
        onPress={() => router.push('/owner/rewards/send-gift')}
        style={{ marginTop: spacing.md }}
      />

      {subs.length > 0 && (
        <View style={{ marginTop: spacing.lg }}>
          <Text style={styles.sectionTitle}>Active Memberships</Text>
          {subs.map(sub => {
            const plan = sub.plan;
            const isMinutes = plan?.planType === 'self_serve'
              || (plan?.maxMinutesPerDay != null || plan?.maxMinutesPerMonth != null);
            const isWashes = plan?.maxWashesPerDay != null || plan?.maxWashesPerMonth != null;
            const periodEnd = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : null;
            return (
              <Card key={sub.id} style={{ padding: spacing.md, marginBottom: spacing.sm }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                  <Ionicons name="ribbon" size={22} color={colors.primary} />
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontWeight: fontWeight.semibold as any, color: colors.text }}>{plan?.name || 'Membership'}</Text>
                    <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{sub.ownerBusinessName}</Text>
                  </View>
                  <View style={{ backgroundColor: (sub.cancelAtPeriodEnd ? colors.warning : colors.success) + '20', paddingHorizontal: spacing.sm, paddingVertical: 2, borderRadius: borderRadius.full }}>
                    <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color: sub.cancelAtPeriodEnd ? colors.warning : colors.success }}>
                      {sub.cancelAtPeriodEnd ? 'Canceling' : 'Active'}
                    </Text>
                  </View>
                </View>

                {(isWashes || isMinutes) && (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginTop: spacing.md }}>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: colors.primary }}>
                        {isMinutes
                          ? `${(sub.minutesUsedToday ?? 0).toFixed(0)}${plan?.maxMinutesPerDay != null ? `/${plan.maxMinutesPerDay}` : ''}`
                          : `${sub.washesUsedToday}${plan?.maxWashesPerDay != null ? `/${plan.maxWashesPerDay}` : ''}`}
                      </Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{isMinutes ? 'min today' : 'today'}</Text>
                    </View>
                    <View style={{ alignItems: 'center' }}>
                      <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: colors.primary }}>
                        {isMinutes
                          ? `${(sub.minutesUsedThisMonth ?? 0).toFixed(0)}${plan?.maxMinutesPerMonth != null ? `/${plan.maxMinutesPerMonth}` : ''}`
                          : `${sub.washesUsedThisMonth}${plan?.maxWashesPerMonth != null ? `/${plan.maxWashesPerMonth}` : ''}`}
                      </Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>{isMinutes ? 'min this month' : 'this month'}</Text>
                    </View>
                  </View>
                )}

                {periodEnd && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: spacing.sm }}>
                    {sub.cancelAtPeriodEnd ? `Access ends ${periodEnd}` : `Renews ${periodEnd}`}
                  </Text>
                )}

                {!sub.cancelAtPeriodEnd && (
                  <>
                    <Button
                      title="Cancel Membership"
                      onPress={() => setConfirmCancel(sub.id)}
                      variant="outline"
                      size="sm"
                      style={{ marginTop: spacing.md }}
                    />
                    {confirmCancel === sub.id && (
                      <InlineConfirm
                        visible={true}
                        message={`Cancel your ${plan?.name || 'membership'} at ${sub.ownerBusinessName}? You'll keep access until the end of your billing period.`}
                        confirmLabel="Cancel Membership"
                        variant="danger"
                        loading={canceling === sub.id}
                        onConfirm={async () => { setConfirmCancel(null); await handleCancelSub(sub); }}
                        onCancel={() => setConfirmCancel(null)}
                      />
                    )}
                  </>
                )}
              </Card>
            );
          })}
        </View>
      )}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.lg },

  // Active wash card
  activeCard: { marginBottom: spacing.lg, backgroundColor: c.success + '10', borderWidth: 1.5, borderColor: c.success + '40' },
  activeHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.sm },
  activePulse: { width: 12, height: 12, borderRadius: 6, backgroundColor: c.success, marginRight: spacing.sm },
  activeTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.success },
  activeLocation: { fontSize: fontSize.md, color: c.textSecondary, marginBottom: spacing.xs },
  activeTimer: { fontSize: fs(40), fontWeight: fontWeight.bold, color: c.text, marginVertical: spacing.sm, fontVariant: ['tabular-nums'] },
  stopButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.danger, borderRadius: borderRadius.md,
    paddingVertical: spacing.sm + 2, gap: spacing.sm, marginTop: spacing.sm,
  },
  stopButtonText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.white },

  // Form
  formCard: { marginBottom: spacing.lg },
  heroRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  formTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  formSubtitle: { fontSize: fontSize.sm, color: c.textSecondary, marginBottom: spacing.md },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs, marginTop: spacing.md },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  chip: {
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.md, borderWidth: 1.5,
    borderColor: c.border, backgroundColor: c.surfaceSecondary,
  },
  chipSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  chipText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  chipTextSelected: { color: c.primary, fontWeight: fontWeight.semibold },
  chipSub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },
  chipSubSelected: { color: c.primary },
  emptyText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic' },
  startButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.primary, borderRadius: borderRadius.md,
    paddingVertical: spacing.md, gap: spacing.sm, marginTop: spacing.lg,
  },
  startButtonDisabled: { opacity: 0.5 },
  startButtonText: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.white },

  // Purchase wash
  purchaseCard: { marginBottom: spacing.lg },
  purchaseTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  scanButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: c.primary, borderRadius: borderRadius.md,
    paddingVertical: spacing.sm + 2, gap: spacing.sm,
  },
  scanButtonText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.white },
  webCameraNote: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs,
    paddingVertical: spacing.sm,
  },
  webCameraText: { fontSize: fontSize.sm, color: '#dc2626' },
  divider: { flexDirection: 'row', alignItems: 'center', marginVertical: spacing.md },
  dividerLine: { flex: 1, height: 1, backgroundColor: c.border },
  dividerText: { marginHorizontal: spacing.sm, fontSize: fontSize.xs, color: c.textLight },
  manualEntry: { flexDirection: 'row', gap: spacing.sm },
  manualInput: {
    flex: 1, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm + 2,
    fontSize: fontSize.sm, color: c.text,
    borderWidth: 1, borderColor: c.border,
  },
  goBtn: {
    width: 44, borderRadius: borderRadius.md,
    backgroundColor: c.primary, justifyContent: 'center', alignItems: 'center',
  },

  // Personal wash history (compact dashboard style)
  historySection: { marginTop: spacing.sm },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  txToolbar: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm },
  txSearchWrap: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6 },
  txSearchInput: { flex: 1, fontSize: fontSize.sm, color: c.text, padding: 0 },
  txCsvBtn: { flexDirection: 'row', alignItems: 'center', backgroundColor: c.primary, borderRadius: borderRadius.sm, paddingHorizontal: spacing.sm, paddingVertical: 6, gap: 4 },
  txCsvText: { color: c.white, fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  txCount: { fontSize: fontSize.xs, color: c.textLight, marginBottom: spacing.sm },
  txRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  txIcon: { width: rs(30), height: rs(30), borderRadius: rs(15), justifyContent: 'center', alignItems: 'center', marginRight: spacing.sm },
  txTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  txDetail: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  txAmount: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  showMoreBtn: { alignItems: 'center', paddingVertical: spacing.md },
  showMoreText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageNav: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm },
  pageBtn: { flexDirection: 'row', alignItems: 'center', gap: 2, paddingVertical: spacing.xs, paddingHorizontal: spacing.sm },
  pageBtnText: { fontSize: fontSize.sm, color: c.primary, fontWeight: fontWeight.medium },
  pageInfo: { fontSize: fontSize.xs, color: c.textSecondary },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
